import { twMerge } from "tailwind-merge";

// Interfaces
import { ReactNode } from "react";
import { IActionButtonProps, IControlledDialogExtension } from "./interfaces.ts";

// Layouts
import Button from "@/Layouts/components/Buttons/Button";

// MUI
import { Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton, Stack, Typography } from "@mui/material";

// Icons
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";

const ActionContainer = (props: Readonly<{ children: ReactNode; sx?: object }>) => {
	const { children, sx = null } = props ?? {};
	return (
		<Stack direction={"row"} spacing={2.5} justifyContent="flex-end" alignItems="center" sx={sx}>
			{children}
		</Stack>
	);
};

const ActionButton = (props: Readonly<IActionButtonProps>) => {
	const {
		title = "Accept",
		variant = "contained",
		classes = "h-[30px] !rounded-[6px]",
		bgColor = "var(--primary-color)",
		disabled = false,
		sx = null,
		onClickAction = () => {},
	} = props ?? {};

	return (
		<Button
			text={title}
			variant={variant}
			classes={twMerge("h-[30px] !rounded-[6px] !font-sans !normal-case", classes)}
			bgColor={bgColor}
			disabled={disabled}
			sx={{ ...sx }}
			onClick={onClickAction}
		/>
	);
};

const DialogExtension = (props: IControlledDialogExtension) => {
	const { children, title = "Modal Title", open = false, actionComponent = null, actions = [], onCloseAction = () => {} } = props;

	return (
		<Dialog
			disableEscapeKeyDown
			fullWidth
			PaperProps={{
				className: "!rounded-[10px]",
			}}
			maxWidth={"lg"}
			open={open}
			onClose={onCloseAction}
		>
			<DialogTitle className="!p-6">
				<Typography className={"!font-montserrat !text-2xl text-default !font-medium"}>{title}</Typography>
				<IconButton
					aria-label="close"
					sx={{
						position: "absolute",
						right: 24,
						top: 20,
						color: (theme) => theme.palette.grey[500],
					}}
					onClick={onCloseAction}
				>
					<CloseRoundedIcon />
				</IconButton>
			</DialogTitle>
			<DialogContent className="h-[38rem] !p-0">{children}</DialogContent>
			<DialogActions className="!p-6">
				<Stack width={"100%"}>
					{Boolean(actions?.length || actionComponent) && (
						<Stack width={"100%"} mb={2.5}>
							<Divider className="!border-disabled" />
						</Stack>
					)}
					<ActionContainer sx={{ marginBottom: "10px" }}>{actionComponent}</ActionContainer>
					<ActionContainer>
						{actions.map((action, index) => {
							const key = `${action?.[title as keyof object]}_${index}`;
							return <ActionButton key={key} {...action} />;
						})}
					</ActionContainer>
				</Stack>
			</DialogActions>
		</Dialog>
	);
};

export default DialogExtension;
